import { Link } from 'react-router-dom'
import { MUIGrid } from '../../Shared'
import { TooltipText } from '../../Shared/TooltipText/TooltipText'
import { CustomTooltip } from '../../Shared/CustomToolTip/CustonToolTip'
import { RedirectIcon, DeleteIcon } from '../../Icons'
import WarningIcon from '../../Icons/WarningIcon'
import { PAGE_URL, PageNames } from '../../../Constants/PagesRoutes/PageURLs'
import { BOLT_MODULE } from '../../../Constants/Roles'
import { CONSTANTS } from '../../../Constants/Constant'
import {
  checkRolePermissionOnLinkClick,
  getDateInFormat,
  disableButtonBasedOnRole,
} from '../../../Helpers/Util'
import { IKeyValue } from '../../../Models/CommonListHeadingModal'
import style from '../../Molecule/Molecule.module.scss'
import headerStyles from './HeaderComponent.module.scss'

type HeaderValue = string | number | IKeyValue[]

interface HeaderContentProps {
  headerData: {
    title: string
    value: HeaderValue
    type?: string
    maxCount?: number
    toolTipWidth?: string
  }[]
  navigate: (path: string) => void
  onDeleteIconClick?: () => Promise<void> | void | undefined
  isDeleted?: any
  status?: string
  layout?: string
}

const { DATE_N_TIME_FORMAT_SECONDS, PAGE_TYPE, DISABLE_DELETE_BUTTON } = CONSTANTS

export default function HeaderContent({
  headerData,
  navigate,
  onDeleteIconClick,
  isDeleted,
  status = 'NA',
  layout = 'NA',
}: HeaderContentProps) {
  const { VIEW } = PAGE_TYPE
  const pathArray = window.location.pathname.split('/')
  const moduleAccess = disableButtonBasedOnRole(pathArray[1])
  const canDelete =
    !DISABLE_DELETE_BUTTON.includes(status?.toLowerCase()) &&
    !DISABLE_DELETE_BUTTON.includes(layout?.toLowerCase())

  const displayArrayItemValue = (value: string, length: number, toolTipWidth?: string) => {
    if (value?.length <= length) return value
    return (
      <CustomTooltip
        title={value}
        placement='bottom'
        arrow
        disableInteractive
        slotProps={{
          tooltip: {
            sx: {
              padding: '0.25rem 0.75rem',
              fontSize: '0.75rem',
              fontWeight: '500',
              lineHeight: '1.5rem',
              color: '#402080',
              bgcolor: '#ECE9F2',
              maxWidth: toolTipWidth ?? '238px',
              width: 'max-content',
              '& .MuiTooltip-arrow': {
                color: '#ECE9F2',
              },
            },
          },
        }}
      >
        <p style={{ margin: 0, cursor: 'pointer' }}>{value?.slice(0, length)}...</p>
      </CustomTooltip>
    )
  }

  const renderArrayValue = (value: IKeyValue[], title?: string, maxCount?: number) => {
    if (value.length === 0) return 'NA'

    return (
      <div className='d-flex align-items-center' style={{ gap: '6px' }}>
        {title?.toLowerCase() === 'segment' ? (
          <Link
            target='_blank'
            to={`${PAGE_URL[PageNames.Segment]}/${VIEW}/${(value?.[0] as any)?.segmentId}`}
            className={headerStyles.tooltipWithBg}
            data-testid='header_segment_value'
            onClick={(event) =>
              checkRolePermissionOnLinkClick(event, BOLT_MODULE.MANAGE_SEGMENTS.key, navigate)
            }
          >
            <RedirectIcon /> {displayArrayItemValue(value?.[0]?.value, maxCount || 25)}
          </Link>
        ) : (
          <p className={headerStyles.segmentPara}>
            {displayArrayItemValue(value?.[0]?.value, maxCount || 25)}
          </p>
        )}

        {value.length > 1 && (
          <CustomTooltip
            title={
              title?.toLowerCase() === 'segment' ? (
                <div className={headerStyles.tooltipBox}>
                  <div className={headerStyles.boxHeader}>
                    <WarningIcon /> <p>Additional Segments ({value.length - 1})</p>
                  </div>
                  <div className={headerStyles.boxContent}>
                    {value.map((item) => (
                      <Link
                        key={(item as any)?.segmentId}
                        target='_blank'
                        to={`${PAGE_URL[PageNames.Segment]}/${VIEW}/${(item as any)?.segmentId}`}
                        onClick={(event) =>
                          checkRolePermissionOnLinkClick(
                            event,
                            BOLT_MODULE.MANAGE_SEGMENTS.key,
                            navigate,
                          )
                        }
                      >
                        <span className={headerStyles.segmentValue}>
                          <RedirectIcon />
                          <TooltipText
                            disableInteractive
                            text={item.value}
                            placement='right'
                            length={30}
                            slotProps={{
                              tooltip: {
                                sx: {
                                  padding: '0.25rem 0.75rem',
                                  position: 'relative',
                                  left: '0.8rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  lineHeight: '1.5rem',
                                  color: '#402080',
                                  bgcolor: '#ECE9F2',
                                  maxWidth: '300px',
                                  width: 'max-content',
                                  zIndex: 1400,
                                  '& .MuiTooltip-arrow': {
                                    color: '#ECE9F2',
                                  },
                                },
                              },
                            }}
                          />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                value.map((item) => item?.value).join(', ')
              )
            }
            placement='bottom'
            slotProps={{
              tooltip: {
                sx: {
                  padding: '0',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  lineHeight: '1.5rem',
                  color: '#402080',
                  bgcolor: '#FFFFFF',
                  width: '285px',
                  maxHeight: '255px',
                  border: '1px solid #C4BAD8',
                  borderRadius: '12px',
                },
              },
            }}
          >
            <div className={`${headerStyles.moreSegment}`}>
              <WarningIcon />
              {`+${value.length - 1} others`}
            </div>
          </CustomTooltip>
        )}
      </div>
    )
  }

  const getContentValues = (
    value?: string | number | IKeyValue[],
    type?: string,
    title?: string,
    maxCount?: number,
    toolTipWidth?: string,
  ) => {
    if (!value) return 'NA'

    if (type === 'date' && typeof value === 'string')
      return getDateInFormat(value, DATE_N_TIME_FORMAT_SECONDS)

    if (Array.isArray(value)) return renderArrayValue(value, title, maxCount)

    if (maxCount !== undefined && typeof value === 'string') {
      return displayArrayItemValue(value, maxCount || 25, toolTipWidth || '238px')
    }

    return String(value)
  }

  const dynamicContentMuiGrid = (data: any) => {
    if (!data || data.length === 0) {
      return null
    }

    return data.map((header: any) => {
      const { title, value, type, maxCount, toolTipWidth } = header

      return (
        <MUIGrid key={title} xs={6} lg={4}>
          <p className={`${headerStyles.headerDataWrapper}`}>
            <span style={{ fontWeight: '400', marginRight: '0.25rem' }} className={'col34'}>
              {`${title} :`}
            </span>
            <span className={`col36 ${style.header_value}`}>
              {getContentValues(value, type, title, maxCount, toolTipWidth)}
            </span>
          </p>
        </MUIGrid>
      )
    })
  }

  return (
    <MUIGrid
      container
      columnSpacing={2}
      rowSpacing={2}
      className={`d-inline-block ${style.asset_header_dynamic_bottom}`}
      style={{ width: '100%', margin: 'auto' }}
    >
      {dynamicContentMuiGrid(headerData)}
      {onDeleteIconClick && !isDeleted && (
        <div
          aria-hidden={true}
          onClick={() => {
            if (!moduleAccess && canDelete) onDeleteIconClick()
          }}
          className={`col37 d-flex align-items-center ${headerStyles.deleteIcon} ${
            !moduleAccess && canDelete ? 'cursor-pointer' : 'disabled'
          }`}
          data-testid={
            !moduleAccess && canDelete ? 'deleteIcon__test' : 'deleteIcon__test_disabled'
          }
        >
          <DeleteIcon />
        </div>
      )}
    </MUIGrid>
  )
}
